import { ActivityParticipationRole, AttendanceType } from "@/pages/activities/activity-types/types";
import { renderAttendanceType, renderParticipationRole, RATE_UNIT_MAP } from "@/pages/activities/activity-types/_utils";
import { CreditRate, RateUnit } from "@/pages/activities/activity-types/types";
import uniqBy from "lodash/uniqBy";

export function isoToLocalDate(iso: string | null | undefined, endOfDay = false): Date | undefined {
  if (!iso) return undefined;
  const [y, m, d] = (iso.split("T")[0] || iso).split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return endOfDay ? new Date(y, m - 1, d, 23, 59, 59, 999) : new Date(y, m - 1, d);
}

export const PARTICIPATION_LEVEL_OPTIONS = (Object.values(AttendanceType) as AttendanceType[]).map((value) => ({
  value,
  label: renderAttendanceType(value),
  displayText: renderAttendanceType(value)
}));

export const getParticipationLevelOptions = (creditRates: CreditRate[]) => {
  return uniqBy(creditRates, "attendanceType").map((creditRate) => ({
    value: creditRate.attendanceType,
    label: renderAttendanceType(creditRate.attendanceType),
    displayText: renderAttendanceType(creditRate.attendanceType)
  }));
};

export const getDefaultParticipationLevel = (creditRates: CreditRate[]): AttendanceType => {
  const options = getParticipationLevelOptions(creditRates);
  return options.length === 1 ? (options[0].value as AttendanceType) : AttendanceType.IN_PERSON;
};

export const getParticipationRoleOptions = (creditRates: CreditRate[]) => {
  const roles =
    creditRates.length > 0
      ? uniqBy(creditRates, "participationRole").map((cr) => cr.participationRole)
      : (Object.values(ActivityParticipationRole) as ActivityParticipationRole[]);
  return roles.map((value) => ({
    value,
    label: renderParticipationRole(value),
    displayText: renderParticipationRole(value)
  }));
};

export const validateDate = (
  value: string,
  name: string,
  allowParticipationStarting: string | null | undefined,
  allowParticipationThrough: string | null | undefined
) => {
  if (!value) return true;
  const minStr = allowParticipationStarting?.split("T")[0]?.trim();
  if (minStr && value < minStr) {
    return `${name} cannot be before the activity start date`;
  }
  const maxStr = allowParticipationThrough?.split("T")[0]?.trim();
  if (maxStr && value > maxStr) {
    return `${name} cannot be after the activity end date`;
  }
  return true;
};

export const findSelectedCreditRate = (
  creditRates: CreditRate[],
  attendanceType?: AttendanceType | string,
  participationRole?: ActivityParticipationRole | string
) =>
  creditRates.find(
    (creditRate) => creditRate.attendanceType === attendanceType && creditRate.participationRole === participationRole
  );

export const shouldShowParticipationQuantity = (rateUnit?: string, rateMultiplier?: number) =>
  !(rateUnit === "INSTANCE" && rateMultiplier !== 1);

export const validateParticipationQuantity = (value: string) => {
  if (!/^\d+(\.\d)?$/.test(value)) return "Must be a positive number with up to one decimal place";
  const num = Number(value);
  if (isNaN(num) || num <= 0) return "Must be a positive number";
  return true;
};

export const getParticipationQuantityLabel = (rateUnit?: string) => {
  if (rateUnit === "INSTANCE") return "PDR Credits";
  const rateUnitLabel = rateUnit ? RATE_UNIT_MAP[rateUnit as RateUnit] : "unit";
  return `Quantity (${rateUnitLabel}s)`;
};

export const formatDefaultParticipationQuantity = (activityDuration?: number | null) => {
  if (activityDuration == null) return "";
  return String(activityDuration);
};
